import {
  FULL_NAME_MAX_LEN,
  BASIC_NAME_EXCLUSIONARY_REGEX,
} from 'constants/validation';
import { FieldValidator } from 'final-form';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import dayjs from './date-helper';
dayjs.extend(customParseFormat);

type ValidationConfig<T = unknown> = {
  input: string;
  rule: T;
  err_msg: string;
};

export const validateRequired = ({
  input,
  rule: is_required = true,
  err_msg,
}: ValidationConfig) => (is_required && input.length < 1 ? err_msg : undefined);

export const validateShorterThan = ({
  input = '',
  rule: min_len,
  err_msg,
}: ValidationConfig<number>) => (input.length < min_len ? undefined : err_msg);

export const validateLongerThan = ({
  input = '',
  rule: max_len,
  err_msg,
}: ValidationConfig<number>) => (input.length > max_len ? undefined : err_msg);

export const validateRegex = ({
  input = '',
  rule: regex,
  err_msg,
}: ValidationConfig<RegExp>) =>
  input.length > 0 && !regex.test(input) ? err_msg : undefined;

export const validateExclusionaryRegex = ({
  input = '',
  rule: regex,
  err_msg,
}: ValidationConfig<RegExp>) =>
  input.length > 0 && input.match(regex) !== null ? err_msg : undefined;

const MIN_PHONE_LENGTH = 10;

export const validatePhoneNumber = ({
  input = '',
  rule: regex,
  err_msg,
}: ValidationConfig<RegExp>) => {
  const digits = input.match(regex);
  return input.length > 0 && digits !== null && digits.length < MIN_PHONE_LENGTH
    ? err_msg
    : undefined;
};

// last argument true is for strict parsing
export const validateDateFormat = ({
  input,
  rule,
  err_msg,
}: ValidationConfig<string>) =>
  dayjs(input, rule, true).isValid() ? undefined : err_msg;

export const composeTextFieldValidators =
  <T = string>(...validators: FieldValidator<T>[]) =>
  (value: T, allvalues: object) =>
    validators.reduce(
      (error, validator) => error || validator(value, allvalues),
      undefined
    );

/**
 * This allows us to abstract the form state validity away of react-final-form, which
 * is useful for niche cases. For example, form state may be invalid from mount (ie:
 * field is blank but required), but we don't want to render the validation error
 * until the user has attempted to submit.
 */
export const resolveFieldError = (
  form_state_valid: boolean,
  {
    touched,
    error,
  }: {
    touched?: boolean;
    error?: string;
  }
) => {
  if ((!form_state_valid && error) || (touched && error)) {
    return error;
  }
};

export const isDateFormatValid = (input: string) =>
  validateDateFormat({
    input: dayjs.utc(input).format('MM/DD/YYYY'),
    err_msg: 'Please enter date in following format (MM/DD/YYYY)',
    rule: 'MM/DD/YYYY',
  });

export const isFirstNameValid = (input: string) =>
  validateShorterThan({
    input,
    rule: FULL_NAME_MAX_LEN,
    err_msg: `Your first name cannot be longer than ${FULL_NAME_MAX_LEN} characters`,
  }) ||
  validateExclusionaryRegex({
    input,
    rule: BASIC_NAME_EXCLUSIONARY_REGEX,
    err_msg: 'Your first name cannot contain any special characters',
  });

export const isLastNameValid = (input: string) =>
  validateShorterThan({
    input,
    rule: FULL_NAME_MAX_LEN,
    err_msg: `Your last name cannot be longer than ${FULL_NAME_MAX_LEN} characters`,
  }) ||
  validateExclusionaryRegex({
    input,
    rule: BASIC_NAME_EXCLUSIONARY_REGEX,
    err_msg: 'Your last name cannot contain any special characters',
  });

export const isPhoneNumberValid = (input: string) =>
  validatePhoneNumber({
    input,
    rule: /\d/g,
    err_msg: 'Your phone number must contain 10 digits',
  });

export const isPhoneNumberEmpty = (input: string) =>
  validateRequired({
    input,
    rule: /\d/g,
    err_msg: 'Your phone number must contain 10 digits',
  });

export const isEmailValid = (input = '') =>
  validateRegex({
    input,
    rule: /\S+@\S+\.\S+/,
    err_msg: 'Please check for misspellings in your email',
  });
